/**
 * Fully synthetic data for Demo Mode. Generated relative to today so the demo
 * always shows five published months, last month in review (so Close Month can be
 * explored) and an open current month. No real figure,
 * name or account appears here.
 */
import { addMonths, currentMonthStart } from '../lib/periods';
import type { MwCockpitRow, MwData } from '../lib/mw';
import type { Account, CapitalPosition, Category, CategoryMonth, Month, ProvisionalTransaction, ReviewItem, Transaction, Upload } from './types';

const LIFESTYLE: [string, number][] = [
  ['Dining & Social', 720_000], ['Food & Groceries', 780_000], ['Travel', 520_000], ['Shopping', 600_000],
  ['Home & Utilities', 390_000], ['Transport', 300_000], ['Health', 420_000], ['Subscriptions & Digital', 180_000],
];
const ACTUALS = [
  [610_000, 745_000, 680_000, 420_000, 368_000, 255_000, 195_000, 162_000],
  [825_000, 710_000, 245_000, 690_000, 355_000, 282_000, 475_000, 158_000],
  [700_000, 765_000, 510_000, 535_000, 402_000, 264_000, 220_000, 155_000],
  [665_000, 720_000, 190_000, 575_000, 380_000, 250_000, 340_000, 150_000],
  [740_000, 690_000, 420_000, 620_000, 365_000, 270_000, 180_000, 148_000],
  [620_000, 735_000, 310_000, 480_000, 395_000, 245_000, 260_000, 145_000],
];

export interface DemoDataset {
  months: Month[];
  categories: Category[];
  categoryMonths: Record<string, CategoryMonth[]>;
  transactions: Record<string, Transaction[]>;
  accounts: Account[];
  positions: CapitalPosition[];
  provisional: Record<string, ProvisionalTransaction[]>;
  mw: MwData;
  mwCockpit: MwCockpitRow[];
  uploads: Record<string, Upload[]>;
  reviewItems: Record<string, ReviewItem[]>;
}

export function buildDemoData(now = new Date()): DemoDataset {
  const open = currentMonthStart(now);
  const asOf = now.toISOString();
  const categories: Category[] = [
    ...LIFESTYLE.map(([name, cap], i) => ({ id: `demo-cat-${i}`, name, category_type: 'lifestyle' as const, monthly_cap: cap, is_active: true })),
    { id: 'demo-cat-inv', name: 'Investments', category_type: 'investment', monthly_cap: null, is_active: true },
    { id: 'demo-cat-sal', name: 'Salary', category_type: 'income', monthly_cap: null, is_active: true },
    { id: 'demo-cat-home', name: 'Housing', category_type: 'fixed', monthly_cap: 1_250_000, is_active: true },
    { id: 'demo-cat-mw', name: 'Migrate World', category_type: 'business', monthly_cap: null, is_active: true },
  ];
  const plan = { investment_target: 1_000_000, personal_spend_cap: 7_200_000, lifestyle_spend_cap: 5_100_000, liquid_reserve_target: 22_500_000 };
  const blank = { investment_actual: null, personal_spend_actual: null, lifestyle_spend_actual: null, liquid_reserve_actual: null };

  const months: Month[] = [{ id: `demo-${open}`, month_start: open, status: 'draft', ...plan, ...blank, monthly_brief: null, next_best_action: null, published_at: null }];
  const categoryMonths: Record<string, CategoryMonth[]> = {};
  const transactions: Record<string, Transaction[]> = {};

  ACTUALS.forEach((actuals, i) => {
    const start = addMonths(open, -(i + 1));
    const id = `demo-${start}`;
    const lifestyle = actuals.reduce((s, v) => s + v, 0);
    const investment = i === 1 ? 850_000 : 1_100_000;
    // Last month is processed but not yet published: the Close Month flow has something to show.
    const inReview = i === 0;
    months.push({
      id, month_start: start, status: inReview ? 'review' : 'published', ...plan,
      investment_actual: investment, lifestyle_spend_actual: lifestyle, personal_spend_actual: lifestyle + 1_250_000,
      liquid_reserve_actual: 12_800_000,
      monthly_brief: inReview ? null : 'Synthetic published month for exploring Finance Cockpit safely.',
      next_best_action: inReview ? null : investment < plan.investment_target ? 'Investment was below target.' : 'Investment target met.',
      published_at: inReview ? null : `${addMonths(start, 1)}T09:00:00Z`,
    });
    categoryMonths[id] = [
      ...LIFESTYLE.map(([name, cap], c) => ({ category_id: `demo-cat-${c}`, name, category_type: 'lifestyle' as const, budget_amount: cap, actual_amount: actuals[c]! })),
      { category_id: 'demo-cat-inv', name: 'Investments', category_type: 'investment', budget_amount: null, actual_amount: investment },
    ];
    transactions[start] = LIFESTYLE.flatMap(([name], c): Transaction[] => {
      const total = actuals[c]!;
      const a = Math.round(total * 0.62);
      return [
        { id: `${id}-${c}-a`, tx_date: `${start.slice(0, 8)}${String(5 + c).padStart(2, '0')}`, description: `Synthetic ${name} purchase`, amount: a, direction: 'debit' as const, currency: 'NGN', classification: name, category_name: name, category_type: 'lifestyle' as const, account_label: 'Example Bank · Demo Current', needs_review: false },
        { id: `${id}-${c}-b`, tx_date: `${start.slice(0, 8)}${String(18 + c).padStart(2, '0')}`, description: `Synthetic ${name} purchase`, amount: total - a, direction: 'debit' as const, currency: 'NGN', classification: name, category_name: name, category_type: 'lifestyle' as const, account_label: 'Example Bank · Demo Current', needs_review: false },
      ];
    }).concat([
      { id: `${id}-sal`, tx_date: `${start.slice(0, 8)}25`, description: 'Synthetic employer salary', amount: 7_400_000, direction: 'credit', currency: 'NGN', classification: 'Salary', category_name: 'Salary', category_type: 'income', account_label: 'Example Bank · Demo Current', needs_review: false },
      { id: `${id}-home`, tx_date: `${start.slice(0, 8)}02`, description: 'Synthetic rent', amount: 1_250_000, direction: 'debit', currency: 'NGN', classification: 'Housing', category_name: 'Housing', category_type: 'fixed', account_label: 'Example Bank · Demo Current', needs_review: false },
      { id: `${id}-inv`, tx_date: `${start.slice(0, 8)}26`, description: 'Synthetic money market top-up', amount: investment, direction: 'debit', currency: 'NGN', classification: 'Investments', category_name: 'Investments', category_type: 'investment', account_label: 'Example Bank · Demo Current', needs_review: false },
    ]).sort((x, y) => y.tx_date.localeCompare(x.tx_date));
  });

  const accounts: Account[] = [
    { id: 'demo-acc-1', name: 'Demo Current', institution: 'Example Bank', account_type: 'bank', currency: 'NGN', current_balance: 4_100_000, alert_bank: 'GTBank', updated_at: asOf },
    { id: 'demo-acc-2', name: 'Demo Money Market', institution: 'Example Investments', account_type: 'money_market', currency: 'NGN', current_balance: 8_700_000, updated_at: asOf },
    { id: 'demo-acc-3', name: 'Demo USD Reserve', institution: 'Example Bank', account_type: 'bank', currency: 'USD', current_balance: 3_250, updated_at: asOf },
  ];
  const positions: CapitalPosition[] = [
    { id: 'demo-pos-1', name: 'Demo Apartment', position_type: 'property', currency: 'NGN', purchase_price: 185_000_000, amount_paid: 145_000_000, outstanding_amount: 40_000_000, current_value: null, owner_funding: null, amount_returned: 0, notes: 'Synthetic property position.', updated_at: asOf },
    { id: 'demo-pos-2', name: 'Demo Venture', position_type: 'business', currency: 'NGN', purchase_price: null, amount_paid: null, outstanding_amount: null, current_value: null, owner_funding: 4_800_000, amount_returned: 0, notes: 'Synthetic owner-funded business.', updated_at: asOf },
  ];
  // Synthetic alerts for the open month, dated no later than today.
  const today = now.getDate();
  const ALERTS: [number, string, number, 'debit' | 'credit'][] = [
    [2, 'POS Synthetic Supermarket', 84_500, 'debit'], [3, 'Synthetic cafe lunch', 23_000, 'debit'],
    [5, 'Synthetic Uber ride', 9_800, 'debit'], [6, 'Synthetic money market top-up', 500_000, 'debit'], [8, 'Synthetic restaurant dinner', 61_000, 'debit'],
  ];
  const provisional: Record<string, ProvisionalTransaction[]> = {
    [open]: ALERTS.filter(([d]) => d <= today).map(([d, description, amount, direction], i) => {
      const tx_date = `${open.slice(0, 8)}${String(d).padStart(2, '0')}`;
      return { id: `demo-alert-${i}`, account_id: 'demo-acc-1', account_label: 'Demo Current', bank: 'GTBank', tx_date,
        occurred_at: `${tx_date}T10:00:00+01:00`, description, amount, direction, currency: 'NGN', status: 'provisional' as const, balance_after: null };
    }).reverse(),
  };
  // Synthetic side venture: history before last month, then Cockpit rows, no payouts yet.
  const last = addMonths(open, -1);
  const mw: MwData = {
    settings: { cockpit_from: last, usd_rate: 1000, eur_rate: 1100 },
    costs: [5, 4, 3, 2].map((back, i) => {
      const d = `${addMonths(open, -back).slice(0, 8)}25`;
      const ngn = [400_000, 650_000, 0, 520_000][i]!;
      return { id: `demo-mw-${i}`, cost_date: d, category: (i === 1 ? 'Office' : 'Payroll') as 'Office' | 'Payroll', description: 'Synthetic venture cost',
        payee: null, currency: 'NGN' as const, amount: ngn || 1, rate_to_ngn: 1, ngn: ngn || 1, source: 'ledger_history' as const, note: null };
    }).filter((c) => c.ngn > 1),
    deals: [
      { id: 'demo-deal-1', name: 'Deal 1', program: 'Program A', option: null, stage: 'committed', currency: 'USD', expected_share: 4_000, share_max: null, notes: null },
      { id: 'demo-deal-2', name: 'Deal 2', program: 'Program B', option: null, stage: 'committed', currency: 'EUR', expected_share: 3_000, share_max: 6_000, notes: null },
      { id: 'demo-deal-3', name: 'Deal 3', program: 'Program A', option: null, stage: 'lead', currency: 'USD', expected_share: 4_000, share_max: null, notes: null },
    ],
    payouts: [],
  };
  // The same Migrate World payments appear in last month's statement transactions and on the tracker.
  const mwCockpit: MwCockpitRow[] = [
    { tx_date: `${last.slice(0, 8)}28`, amount: 600_000, currency: 'NGN', description: 'Synthetic MW salary', from: 'statement' },
    { tx_date: `${last.slice(0, 8)}12`, amount: 150_000, currency: 'NGN', description: 'Synthetic MW recruit fee', from: 'statement' },
  ];
  transactions[last] = [...(transactions[last] ?? []), ...mwCockpit.map((r, i): Transaction => ({
    id: `demo-mw-tx-${i}`, tx_date: r.tx_date, description: r.description, amount: r.amount, direction: 'debit', currency: 'NGN',
    classification: 'Migrate World', category_name: 'Migrate World', category_type: 'business', account_label: 'Example Bank · Demo Current',
    needs_review: false, is_business_funding: true,
  }))].sort((x, y) => y.tx_date.localeCompare(x.tx_date));
  // Synthetic statements and review items for the month in review.
  const uploads: Record<string, Upload[]> = {
    [`demo-${last}`]: ['Example Bank current', 'Example Bank card', 'Example Investments'].map((name, i): Upload => ({
      id: `demo-up-${i}`, month_id: `demo-${last}`, account_id: null, storage_path: '', original_filename: `Synthetic ${name} statement.pdf`,
      file_size_bytes: null, processing_status: 'processed', processing_error: null, transaction_count: [24, 11, 3][i]!,
      uploaded_at: `${open}T08:00:00Z`, processed_at: `${open}T08:05:00Z`,
    })),
  };
  const reviewItems: Record<string, ReviewItem[]> = {
    [last]: [
      { id: 'demo-rev-1', question: null, tx_date: `${last.slice(0, 8)}14`, description: 'Synthetic transfer to unknown payee', amount: 120_000, direction: 'debit', currency: 'NGN', classification: null },
      { id: 'demo-rev-2', question: 'Same amount left another of your accounts on the same day.', tx_date: `${last.slice(0, 8)}20`, description: 'Synthetic incoming transfer', amount: 300_000, direction: 'credit', currency: 'NGN', classification: null },
    ],
  };
  return { months, categories, categoryMonths, transactions, accounts, positions, provisional, mw, mwCockpit, uploads, reviewItems };
}
